import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { SITE } from "@/config/site";
import { Seo, localBusinessSchema } from "@/components/Seo";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { ContactForm } from "@/components/ContactForm";import { trackEvent } from "@/lib/analytics";

export default function Contact() {
  const crumbs = [{ label: "Contact" }];
  return (
    <>
      <Seo
        title="Contact & demande d'intervention dans la Manche | FRELONS50"
        description="Contactez FRELONS50 pour une intervention contre les frelons, guêpes, rats ou souris dans la Manche. Appelez le 06 35 92 21 49 ou remplissez le formulaire avec photos."
        path="/contact"
        jsonLd={[localBusinessSchema, breadcrumbSchema(crumbs)]}
      />

      <section className="bg-brand-black text-white grain-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="[&_*]:text-white/80 mb-5"><Breadcrumbs items={crumbs} /></div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08]">
            Contact & demande d'intervention
          </h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl leading-relaxed">
            Décrivez-nous la situation en quelques secondes et laissez-nous vos coordonnées. Nous vous recontactons dans
            les meilleurs délais. En cas d'urgence, appelez-nous directement.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 grid lg:grid-cols-[1fr_340px] gap-10">
        <div className="order-2 lg:order-1">
          <ContactForm />
        </div>

        {/* Sidebar contact direct */}
        <aside className="order-1 lg:order-2 space-y-4">
          <div className="bg-brand-gray rounded-3xl p-6">
            <h2 className="font-display text-lg font-bold text-brand-black mb-4">Contact direct</h2>
            <a href={SITE.phoneHref} onClick={() => trackEvent("click_phone", { location: "contact_side" })} data-testid="contact-phone" className="flex items-center gap-3 py-3 group">
              <span className="w-11 h-11 rounded-full bg-brand-gold flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-brand-black" /></span>
              <span><span className="block text-xs text-brand-text">Téléphone</span><span className="font-bold text-brand-black group-hover:underline">{SITE.phoneDisplay}</span></span>
            </a>
            <a href={SITE.emailHref} onClick={() => trackEvent("click_email", { location: "contact_side" })} data-testid="contact-email" className="flex items-center gap-3 py-3 group">
              <span className="w-11 h-11 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-brand-black" /></span>
              <span className="min-w-0"><span className="block text-xs text-brand-text">E-mail</span><span className="font-bold text-brand-black group-hover:underline break-all text-sm">{SITE.email}</span></span>
            </a>
            {SITE.whatsappEnabled && (
              <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_whatsapp", { location: "contact_side" })} data-testid="contact-whatsapp" className="flex items-center gap-3 py-3 group">
                <span className="w-11 h-11 rounded-full bg-[#25D366] flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5 text-white" /></span>
                <span><span className="block text-xs text-brand-text">WhatsApp</span><span className="font-bold text-brand-black group-hover:underline">Écrire un message</span></span>
              </a>
            )}
          </div>

          <div className="bg-white border border-gray-100 rounded-3xl p-6">
            <h2 className="font-display text-lg font-bold text-brand-black mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-brand-gold" /> Disponibilité</h2>
            {SITE.hours.map((h) => (
              <div key={h.day} className="flex justify-between py-1.5 text-sm border-b border-gray-100 last:border-0">
                <span className="text-brand-text">{h.day}</span><span className="font-semibold text-brand-black">{h.value}</span>
              </div>
            ))}
            <div className="mt-4 flex items-center gap-2 text-sm text-brand-text"><MapPin className="w-4 h-4 text-brand-gold" /> {SITE.departement}</div>
            {SITE.social.mapsDirections && (
              <a href={SITE.social.mapsDirections} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-brand-black font-semibold text-sm underline">Itinéraire →</a>
            )}
          </div>
        </aside>
      </section>
    </>
  );
}
